import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Truck, Download, Loader2, Send, Plus, ExternalLink, CheckCircle2, ClipboardList, Image as ImageIcon, Trash2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import ReportesPendientesAlbaranPanel from "@/components/ReportesPendientesAlbaranPanel";
import FdCicloPanel from "@/components/FdCicloPanel";
import { ShieldCheck } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);
const syncEstado = (a) => a.fd_sync_estado || (a.fd_delivery_note_id ? "sincronizado" : "no_enviado");

export default function Albaranes() {
  const [tab, setTab] = useState(() => localStorage.getItem("dnf-albaranes-tab") || "pendientes");
  useEffect(() => { localStorage.setItem("dnf-albaranes-tab", tab); }, [tab]);

  const [albs, setAlbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enviandoFd, setEnviandoFd] = useState(null); // {id, modo}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/albaranes");
      setAlbs(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando albaranes");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Detectar albaranes locales huérfanos (sin FD) para banner de limpieza.
  const huerfanos = useMemo(
    () => albs.filter((a) => !a.fd_delivery_note_id && a.estado !== "anulado" && !["incierto", "enviando"].includes(a.fd_sync_estado)),
    [albs]
  );
  const [limpiando, setLimpiando] = useState(false);
  const limpiarHuerfanos = async () => {
    if (huerfanos.length === 0) return;
    const total = huerfanos.reduce((s, a) => s + Number(a.totales?.total || 0), 0);
    const ok = window.confirm(
      `Se van a eliminar ${huerfanos.length} albaranes locales SIN albarán FD asociado.\n\n` +
      `Total: ${eur(total)}\n\n` +
      `Suelen ser intentos de envío fallidos. Las paradas asociadas vuelven a estar "pendientes de albaranar". ¿Continuar?`
    );
    if (!ok) return;
    setLimpiando(true);
    try {
      const { data } = await api.post("/albaranes/limpiar-huerfanos", {
        ids: huerfanos.map((a) => a.id),
      });
      toast.success(`Eliminados ${data.borrados} albaranes locales huérfanos`);
      if ((data.fallos || []).length > 0) {
        toast.warning(`${data.fallos.length} no se pudieron eliminar`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error limpiando huérfanos");
    } finally { setLimpiando(false); }
  };

  const enviarFd = async (alb, nuevo = false) => {
    if (nuevo) {
      if (!window.confirm(`Crear un albarán NUEVO en FacturaDirecta para ${alb.cliente_snapshot?.razon_social}?\n\nSerá un albarán APARTE: no se fusionará con el del ciclo ni recibirá otros montajes.`)) return;
    }
    setEnviandoFd({ id: alb.id, modo: nuevo ? "nuevo" : "auto" });
    try {
      const url = `/albaranes/${alb.id}/enviar-fd${nuevo ? "?nuevo=true" : ""}`;
      const { data } = await api.post(url);
      toast.success(data.mensaje || `Albarán enviado a FD: ${data.fd_number}`);
      load();
    } catch (e) {
      const st = e?.response?.status;
      toast[st === 409 || st === 504 ? "warning" : "error"](e?.response?.data?.detail || "Error enviando a FD", { duration: 8000 });
      load();
    } finally { setEnviandoFd(null); }
  };

  const reconciliar = async (alb) => {
    setEnviandoFd({ id: alb.id, modo: "reconciliar" });
    try {
      const { data } = await api.post(`/fd-albaranes/albaran/${alb.id}/reconciliar`);
      toast[data.resultado === "incierto" ? "warning" : "success"](`${data.resultado}: ${data.mensaje}`, { duration: 8000 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error reconciliando");
    } finally { setEnviandoFd(null); }
  };

  const descargar = (aid, numero) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("access_token");
    fetch(`${base}/api/albaranes/${aid}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `${numero}.pdf`; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Error descargando PDF"));
  };

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="albaranes-page">
      <PageHeader
        title="Albaranes"
        subtitle="Sincronización automática con FacturaDirecta · 1 albarán/mes/cliente"
        icon={Truck}
        action={
          <a
            href="/portfolio" target="_blank" rel="noreferrer"
            data-testid="link-portfolio-publico"
            className="text-xs px-3 py-1.5 rounded border border-zinc-700 text-zinc-300 hover:text-amber-300 hover:border-amber-500/40 inline-flex items-center gap-1.5"
          >
            <ImageIcon className="w-3.5 h-3.5" /> Ver book público
          </a>
        }
      />

      {/* Tabs Pendientes / Albaranes ya creados */}
      <div className="inline-flex rounded-lg border border-zinc-800 bg-zinc-900/40 p-0.5"
        data-testid="albaranes-tabs">
        <button
          type="button"
          onClick={() => setTab("pendientes")}
          data-testid="tab-pendientes"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs transition ${
            tab === "pendientes"
              ? "bg-amber-500 text-zinc-900 font-semibold"
              : "text-zinc-400 hover:text-zinc-100"
          }`}>
          <ClipboardList className="w-3.5 h-3.5" /> Pendientes de albaranar
        </button>
        <button
          type="button"
          onClick={() => setTab("creados")}
          data-testid="tab-creados"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs transition ${
            tab === "creados"
              ? "bg-amber-500 text-zinc-900 font-semibold"
              : "text-zinc-400 hover:text-zinc-100"
          }`}>
          <Truck className="w-3.5 h-3.5" /> Albaranes ya creados ({albs.length})
        </button>
        <button
          type="button"
          onClick={() => setTab("ciclo")}
          data-testid="tab-ciclo-fd"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs transition ${
            tab === "ciclo"
              ? "bg-amber-500 text-zinc-900 font-semibold"
              : "text-zinc-400 hover:text-zinc-100"
          }`}>
          <ShieldCheck className="w-3.5 h-3.5" /> Ciclo FD · cierre de mes
        </button>
      </div>

      {tab === "pendientes" ? (
        <ReportesPendientesAlbaranPanel onChange={load} />
      ) : tab === "ciclo" ? (
        <FdCicloPanel />
      ) : (
      <>
      {huerfanos.length > 0 && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-3 text-xs text-rose-100 flex items-center justify-between gap-3 flex-wrap"
          data-testid="banner-huerfanos">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 text-rose-400" />
            <div>
              <b>{huerfanos.length} albaranes locales sin FD detectados</b>
              <div className="text-rose-200/80 mt-0.5">
                Intentos fallidos previos (cliente sin sync FD, etc.) generaron albaranes locales huérfanos por valor de <b>{eur(huerfanos.reduce((s, a) => s + Number(a.totales?.total || 0), 0))}</b>. Puedes eliminarlos: las paradas vuelven a quedar como "pendientes" y al reintentar el envío ya NO se duplicarán (fix idempotente activo).
              </div>
            </div>
          </div>
          <Button
            size="sm" onClick={limpiarHuerfanos} disabled={limpiando}
            data-testid="btn-limpiar-huerfanos"
            className="bg-rose-500 hover:bg-rose-600 text-white font-semibold"
          >
            {limpiando ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
            Eliminar los {huerfanos.length}
          </Button>
        </div>
      )}

      <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 text-xs text-cyan-100" data-testid="info-flujo-fd">
        <b>🔄 Flujo blindado:</b> al pulsar <b>Enviar a FD</b> la app añade las líneas al albarán FD que DNF tiene registrado como <b>Abierto</b> para ese cliente en el ciclo vigente
        (ver pestaña <b>Ciclo FD</b>). Si no hay ninguno abierto, lo crea. Nunca reutiliza albaranes cerrados, desconocidos o modificados fuera de DNF. Un albarán ya sincronizado no se reenvía.
        <br />
        <b>Nuevo</b> crea un albarán FD aparte (útil si el cliente lo pide separado). Antes de facturar en FD, <b>cierra</b> el albarán en la pestaña Ciclo FD.
      </div>

      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-zinc-500"><Loader2 className="w-6 h-6 animate-spin mr-2" />Cargando…</div>
        ) : albs.length === 0 ? (
          <div className="text-center py-16 text-zinc-500 text-sm">
            <Truck className="w-10 h-10 mx-auto mb-3 opacity-40" />
            No hay albaranes todavía.<br/>
            <span className="text-[10px]">Se generan automáticamente al entregar peticiones o desde un presupuesto aceptado.</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase text-zinc-500 border-b border-zinc-800">
                  <th className="px-3 py-2.5">Nº local</th>
                  <th className="px-3 py-2.5">Cliente</th>
                  <th className="px-3 py-2.5">Fecha</th>
                  <th className="px-3 py-2.5 text-right">Total</th>
                  <th className="px-3 py-2.5">Estado</th>
                  <th className="px-3 py-2.5">FD</th>
                  <th className="px-3 py-2.5 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {albs.map((a) => {
                  const cliente = (a.cliente_snapshot || {}).razon_social || "—";
                  const sync = syncEstado(a);
                  const enviado = sync === "sincronizado";
                  const incierto = sync === "incierto" || sync === "enviando";
                  const isBusy = enviandoFd?.id === a.id;
                  return (
                    <tr key={a.id} className="border-b border-zinc-800/60 hover:bg-zinc-800/30" data-testid={`fila-albaran-${a.numero}`}>
                      <td className="px-3 py-2.5 font-mono text-amber-300 font-semibold">{a.numero}</td>
                      <td className="px-3 py-2.5 text-zinc-200">{cliente}</td>
                      <td className="px-3 py-2.5 text-zinc-400 text-xs">{a.fecha_emision}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-zinc-100">{eur(a.totales?.total)}</td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded border ${
                          a.estado === "facturado" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" :
                          a.estado === "anulado" ? "bg-rose-500/20 text-rose-300 border-rose-500/40" :
                          "bg-amber-500/20 text-amber-300 border-amber-500/40"
                        }`}>{a.estado === "pendiente_facturar" ? "Pendiente" : a.estado}</span>
                      </td>
                      <td className="px-3 py-2.5 text-xs">
                        {enviado ? (
                          <span className="inline-flex items-center gap-1 text-emerald-300 font-mono"
                            data-testid={`fd-num-${a.numero}`} title={a.fd_periodo ? `Ciclo ${a.fd_periodo} · ${a.fd_sync_action || ""}` : ""}>
                            <CheckCircle2 className="w-3 h-3" />
                            {a.fd_delivery_note_number || "(sin nº)"}
                            <a href={`https://app.facturadirecta.com/`} target="_blank" rel="noreferrer"
                              className="text-zinc-500 hover:text-emerald-300 ml-0.5" title="Abrir FacturaDirecta">
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </span>
                        ) : incierto ? (
                          <span className="inline-flex items-center gap-1 text-amber-300" data-testid={`fd-incierto-${a.numero}`} title={a.fd_sync_error || "Envío interrumpido"}>
                            <AlertTriangle className="w-3 h-3" /> Incierto · reconciliar
                          </span>
                        ) : (
                          <span className="text-zinc-600 italic" data-testid={`fd-no-enviado-${a.numero}`}>— no enviado —{a.fd_sync_error ? <span className="block not-italic text-amber-300/80 truncate max-w-[220px]" title={a.fd_sync_error}>{a.fd_sync_error}</span> : null}</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <Button size="sm" variant="ghost"
                            onClick={() => descargar(a.id, a.numero)}
                            data-testid={`btn-descargar-alb-${a.numero}`}
                            title="Descargar PDF interno"
                            className="h-7 px-2 text-zinc-400 hover:text-amber-300">
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                          {incierto ? (
                            <Button size="sm"
                              onClick={() => reconciliar(a)}
                              disabled={isBusy}
                              data-testid={`btn-reconciliar-fd-${a.numero}`}
                              title="Comprobar en FD si las líneas llegaron y resolver el estado (no escribe en FD)"
                              className="h-7 px-2 text-[11px] font-semibold bg-amber-500 hover:bg-amber-400 text-zinc-950">
                              {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><AlertTriangle className="w-3 h-3 mr-1" /> Reconciliar</>}
                            </Button>
                          ) : enviado ? (
                            <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-emerald-500/30 text-emerald-300/80" data-testid={`fd-sincronizado-${a.numero}`} title="Ya sincronizado: no se reenvía para evitar duplicados">Sincronizado</span>
                          ) : (
                            <>
                              <Button size="sm"
                                onClick={() => enviarFd(a, false)}
                                disabled={isBusy || a.estado === "anulado"}
                                data-testid={`btn-enviar-fd-${a.numero}`}
                                title="Añadir al albarán FD abierto del ciclo (o crear si no existe)"
                                className="h-7 px-2 text-[11px] font-semibold bg-emerald-500 hover:bg-emerald-400 text-zinc-950">
                                {isBusy && enviandoFd?.modo === "auto"
                                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  : <><Send className="w-3 h-3 mr-1" /> Enviar a FD</>}
                              </Button>
                              <Button size="sm" variant="outline"
                                onClick={() => enviarFd(a, true)}
                                disabled={isBusy || a.estado === "anulado"}
                                data-testid={`btn-nuevo-fd-${a.numero}`}
                                title="Crear un albarán FD aparte (no se fusionará)"
                                className="h-7 px-2 text-[11px] bg-transparent border-orange-500/40 text-orange-300 hover:bg-orange-500/20">
                                {isBusy && enviandoFd?.modo === "nuevo"
                                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  : <><Plus className="w-3 h-3 mr-0.5" /> Nuevo</>}
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}
